import { createColumnHelper } from '@tanstack/react-table';
import React, { Dispatch, SetStateAction } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatNumericValue } from 'utils/general';

import { IContracts } from '@/modules/workforce/Contracts/types';
import { Table } from '@/shared/components/atoms';
import IconWithTitleDescription from '@/shared/components/atoms/IconWithTItleDescription';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import ReviewCard from '@/shared/components/molecules/MultiStepForm/ReviewCard';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { getLocalizedDate } from '@/shared/lib/dates';

import Building from '~/images/contract/building.svg';
import Clock from '~/images/contract/clock.svg';
import Coin from '~/images/contract/coin.svg';
import Line from '~/images/contract/Line.svg';
import Receipt from '~/images/contract/receipt.svg';
import Scissors from '~/images/contract/scissors.svg';
import Supplier from '~/images/contract/supplier.svg';

const ContractReview: React.FC<{
  setStep: Dispatch<SetStateAction<number>>;
}> = ({ setStep }) => {
  const { watch } = useFormContext<IContracts.IAddContractSchema>();

  const dailyWage = watch('dailyWage');
  const endDate = watch('endDate');
  const entity = watch('entity');
  const startDate = watch('startDate');
  const jobs = watch('jobs');
  const supplier = watch('supplier');
  const dailyBased = watch('dailyBased');
  const unitBased = watch('unitBased');
  const hasExtraTime = watch('dailyBased.hasExtraTime');

  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const sharedCardProps = {
    titleTypography: {
      variant: 'caption' as const,
    },
    descriptionTypography: {
      weight: 'medium' as const,
    },
    iconGap: 'gap-16',
  };

  const contractDetailsCardItems: IIconWithTitleDescriptionProps[] = [
    {
      icon: <Building width='24' height='24' />,
      title: t('farm'),
      description: entity.name,
    },
    {
      icon: <Supplier width='24' height='24' />,
      title: t('crew'),
      description: supplier.name,
    },
    {
      icon: <Line width='24' height='24' />,
      title: t('contract-duration'),
      description: `${getLocalizedDate({
        date: startDate,
        formatDate: 'dd MMM yyyy',
        locale: language,
      })} - ${getLocalizedDate({
        date: endDate,
        formatDate: 'dd MMM yyyy',
        locale: language,
      })}`,
    },
    {
      icon: <Scissors width='24' height='24' />,
      title: t('operations'),
      description: (
        <PartialListWithTooltip
          containerClassName='flex-wrap'
          limit={10}
          items={jobs.map((job) => ({
            description: job.label,
            imageURL: job.cropImage,
          }))}
        />
      ),
    },
  ];

  const dailyBasedCardItems: IIconWithTitleDescriptionProps[] = [
    {
      icon: <Coin width='24' height='24' />,
      title: t('daily-rate'),
      description: `${dailyBased?.rate}${
        entity?.currency ? ` ${t(entity?.currency)}` : ''
      }`,
    },
    {
      icon: <Clock width='24' height='24' />,
      title: t('working-hours'),
      description: `${getLocalizedDate({
        date: dailyBased?.startTime,
        formatDate: 'hh:mm a',
        locale: language,
      })} - ${getLocalizedDate({
        date: dailyBased?.endTime,
        formatDate: 'hh:mm a',
        locale: language,
      })}`,
    },
    ...(hasExtraTime
      ? [
          {
            icon: <Coin width='24' height='24' />,
            title: t('extra-time'),
            description: `${dailyBased?.extraHoursPricing?.[0]?.hourPrice} ${t(
              entity?.currency
            )}`,
          },
        ]
      : []),
  ];

  const columnHelper = createColumnHelper<IContracts.IContracUnitBasedSchema>();
  const unitBasedTableColumn = [
    columnHelper.display({
      id: 'unitIdx',
      header: t('units'),
      cell: ({ row }) => `${t('unit')} ${row.index + 1}`,
    }),
    columnHelper.accessor('unitName', {
      header: t('unit-type'),
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor('unitPrice', {
      header: t('unit-value'),
      cell: ({ getValue }) =>
        `${formatNumericValue(getValue() as number)}${
          entity.currency ? ` ${t(entity.currency)}` : ''
        }`,
    }),
  ];

  return (
    <div className='flex flex-col gap-16'>
      <ReviewCard header={t('contract-details')} onClick={() => setStep(0)}>
        {contractDetailsCardItems.map((item, idx) => (
          <IconWithTitleDescription
            key={`${idx}-${item.title}`}
            item={{ ...sharedCardProps, ...item }}
          />
        ))}
      </ReviewCard>
      <ReviewCard
        header={t('contract-type')}
        onClick={() => setStep(1)}
        cols={dailyWage ? 'grid-cols-2' : 'grid-cols-1'}
      >
        <IconWithTitleDescription
          item={{
            ...sharedCardProps,
            icon: <Receipt width='24' height='24' />,
            title: t('contract-type'),
            description: t(dailyWage ? 'daily-rate' : 'piecework-rate'),
          }}
        />
        {dailyWage ? (
          dailyBasedCardItems.map((item, idx) => (
            <IconWithTitleDescription
              key={`${item.title}-${idx}`}
              item={{ ...sharedCardProps, ...item }}
            />
          ))
        ) : (
          <Table<IContracts.IContracUnitBasedSchema>
            pagination={false}
            selection={false}
            data={unitBased as IContracts.IContracUnitBasedSchema[]}
            columns={unitBasedTableColumn}
            headCellClassName='text-start border-b-xs border-neutralVariant-90 text-surface-10 font-semi-bold'
            headContainerClassName='!bg-background-transparent'
            tableWrapperClassName='!rounded-xl border-xs border-neutralVariant-90'
            rowContainerClassName='border-b-xs border-neutralVariant-90 last:border-0'
            tableClassName='!border-collapse'
          />
        )}
      </ReviewCard>
    </div>
  );
};

export default ContractReview;
